import React from "react";
import { Stack, Typography, Alert, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SendIcon from "@mui/icons-material/Send";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { forgotPassword } from "../redux/actions/authActions";
import { authActions } from "../redux/slices/auth";
import { cleanFormData } from "../helpers/formHelpers";

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
});

interface FormValues {
  email: string;
}

const ForgotPasswordForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const { loadingForgotPassword, errorForgotPassword, serverStatus } =
    useAppSelector((state) => state.auth);

  const isSent =
    serverStatus?.operation === "forgot-password" &&
    serverStatus?.status === 200;

  if (isSent) {
    return (
      <Alert severity="success" sx={{ textAlign: "left" }}>
        If an account exists for that email, a password reset link has been
        sent. Please check your inbox (and spam folder).
      </Alert>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Formik
        initialValues={{ email: "" }}
        validationSchema={forgotPasswordSchema}
        onSubmit={(values: FormValues) => {
          const cleaned = cleanFormData(values) as FormValues;
          dispatch(forgotPassword(cleaned));
        }}
      >
        {({ isValid }: FormikProps<FormValues>) => (
          <Form>
            <Stack spacing={3}>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", textAlign: "center" }}
              >
                Enter your registered email address and we'll send you a link to
                reset your password.
              </Typography>

              {errorForgotPassword && (
                <Alert severity="error" sx={{ textAlign: "left" }}>
                  {errorForgotPassword}
                </Alert>
              )}

              <FormTextField
                name="email"
                label="Email Address"
                type="email"
                placeholder="yourname@school.com"
                slotProps={{
                  input: {
                    startAdornment: (
                      <EmailOutlinedIcon
                        sx={{ color: "action.active", mr: 1 }}
                      />
                    ),
                  },
                }}
              />

              <SubmitButton
                loading={loadingForgotPassword}
                title="Send Reset Link"
                loadingText="Sending..."
                disabled={!isValid || loadingForgotPassword}
                startIcon={<SendIcon />}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default ForgotPasswordForm;
